/**
 * <biosphere-scroll-area> — a drawn scrollbar for the sideways-scrolling viewport
 * rendered by snippets/biosphere-scroll-area.liquid.
 *
 * The viewport scrolls natively with or without this; what it adds is a bar every
 * browser shows. WebKit on iOS hides overflow scrollbars until the reader is already
 * swiping, and no style changes that, so the track is an element of its own. It is
 * revealed only while the content overflows, and the CSS hides the native bar while
 * it shows.
 *
 * The thumb follows the viewport, never the other way round: dragging it or tapping
 * the track sets scrollLeft, and the scroll event moves the thumb. Motion is CSS's —
 * a tap on the track uses scrollTo and inherits the viewport's scroll-behavior.
 */
class BiosphereScrollArea extends HTMLElement {
  #viewport;
  #track;
  #thumb;
  #abort;
  #observer;
  #frame = 0;
  #thumbSize = 0;

  connectedCallback() {
    this.#viewport = this.querySelector(".biosphere-scroll-area__viewport");
    this.#track = this.querySelector(".biosphere-scroll-area__track");
    this.#thumb = this.querySelector(".biosphere-scroll-area__thumb");

    // Without its parts the native scroller stands, which is the baseline.
    if (!this.#viewport || !this.#track || !this.#thumb) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#viewport.addEventListener("scroll", () => this.#schedule(), {
      signal,
      passive: true,
    });

    // The viewport resizes with the page; its content resizes when a hidden
    // panel it sits in is revealed. Either can start or stop an overflow.
    this.#observer = new ResizeObserver(() => this.#schedule());
    this.#observer.observe(this.#viewport);
    if (this.#viewport.firstElementChild) {
      this.#observer.observe(this.#viewport.firstElementChild);
    }

    this.#thumb.addEventListener("pointerdown", (event) => this.#drag(event), {
      signal,
    });

    this.#track.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target === this.#track) this.#jump(event);
      },
      { signal },
    );

    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** One measurement per frame, however many scroll events arrive in it. */
  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.#viewport;
    const range = scrollWidth - clientWidth;

    // A pixel of rounding is not an overflow.
    this.#track.hidden = range <= 1;
    if (this.#track.hidden) return;

    const trackWidth = this.#track.clientWidth;
    this.#thumbSize = Math.max(
      trackWidth * (clientWidth / scrollWidth),
      trackWidth * 0.1,
    );

    // abs(): scrollLeft runs negative in a right-to-left document.
    const offset =
      (Math.abs(scrollLeft) / range) * (trackWidth - this.#thumbSize);

    this.#thumb.style.setProperty(
      "--scroll-area-thumb-size",
      `${this.#thumbSize}px`,
    );
    this.#thumb.style.setProperty("--scroll-area-thumb-offset", `${offset}px`);
  }

  /** Drag the thumb; the viewport scrolls by the same share of its range. */
  #drag(event) {
    event.preventDefault();

    const range = this.#viewport.scrollWidth - this.#viewport.clientWidth;
    const travel = this.#track.clientWidth - this.#thumbSize;
    if (range <= 0 || travel <= 0) return;

    const startX = event.clientX;
    const startScroll = this.#viewport.scrollLeft;
    const ratio = range / travel;

    // One drag's listeners, torn down on release or with the element.
    const drag = new AbortController();
    this.#abort.signal.addEventListener("abort", () => drag.abort(), {
      signal: drag.signal,
    });

    this.#thumb.setPointerCapture(event.pointerId);

    this.#thumb.addEventListener(
      "pointermove",
      (move) => {
        this.#viewport.scrollLeft =
          startScroll + (move.clientX - startX) * ratio;
      },
      { signal: drag.signal },
    );

    for (const end of ["pointerup", "pointercancel"]) {
      this.#thumb.addEventListener(end, () => drag.abort(), {
        signal: drag.signal,
      });
    }
  }

  /** Tap the track: centre the view on the point tapped. */
  #jump(event) {
    const rect = this.#track.getBoundingClientRect();
    const share = (event.clientX - rect.left) / rect.width;
    const { scrollWidth, clientWidth } = this.#viewport;

    this.#viewport.scrollTo({ left: share * scrollWidth - clientWidth / 2 });
  }
}

if (!customElements.get("biosphere-scroll-area")) {
  customElements.define("biosphere-scroll-area", BiosphereScrollArea);
}
